/**
 * Reading a storefront out of the host it was requested on.
 *
 * Every store is published at `{subdomain}.{rootDomain}`, so the host alone
 * says which storefront a visitor asked for. Kept free of Nuxt composables:
 * the router needs this before the app exists, on both server and client.
 */

/**
 * Platform labels that must never be treated as a tenant storefront.
 *
 * Mirrored from `backend/config/stores.php` → `reserved_subdomains`
 * (`App\Support\ReservedStorefrontSubdomains`). The backend is
 * authoritative; keep this list identical when either side changes.
 */
export const RESERVED_STOREFRONT_SUBDOMAINS = [
  'www',
  'api',
  'admin',
  'app',
  'login',
  'support',
  'static',
  'cdn',
  'mail',
  'checkout',
  'auth',
  'media',
  'assets',
] as const

export function isReservedStorefrontSubdomain(label: string): boolean {
  return (RESERVED_STOREFRONT_SUBDOMAINS as readonly string[]).includes(
    label.trim().toLowerCase(),
  )
}

/** Direct IP access (`127.0.0.1:3000`) is the platform, never a storefront. */
function isIpHost(hostname: string): boolean {
  return hostname.includes(':') || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname)
}

function hostnameOf(host: string): string {
  // Bracketed IPv6 (`[::1]:3000`) keeps its colons; a plain host loses the port.
  return host.startsWith('[')
    ? host.slice(0, host.indexOf(']') + 1)
    : (host.split(':')[0] ?? '')
}

/**
 * The storefront label in `host`, or `null` when this is the platform itself.
 *
 * With `rootDomain` set, anything sitting directly under it is a storefront.
 * Without it the base is assumed to be two labels (`vntru.com`), or
 * the single label `localhost` — which is what local development runs on,
 * since browsers already point every `*.localhost` name at the loopback
 * address.
 */
export function storeSubdomainFrom(host: string, rootDomain = ''): string | null {
  const hostname = hostnameOf(host.trim().toLowerCase())
  if (!hostname || isIpHost(hostname)) return null

  // Ports (`localhost:3000`) and a `www.` site origin must not hide the shop.
  const base = hostnameOf(rootDomain.trim().toLowerCase()).replace(/^www\./, '')
  const label = base
    ? labelUnder(hostname, base)
    : labelUnderAssumedBase(hostname)

  return label && !isReservedStorefrontSubdomain(label) ? label : null
}

/** Job-platform surfaces that must never answer on a store subdomain. */
export function isJobPlatformPath(path: string): boolean {
  return path === '/app'
    || path.startsWith('/app/')
    || path === '/jobs'
    || path.startsWith('/jobs/')
    || path === '/apply'
    || path.startsWith('/apply/')
    || path === '/hire'
    || path.startsWith('/hire/')
    || path === '/sign-in'
    || path === '/sign-up'
}

/** Single label sitting directly under a known base host. */
function labelUnder(host: string, base: string): string | null {
  if (!host.endsWith(`.${base}`)) return null

  const label = host.slice(0, -(base.length + 1))

  return label.includes('.') ? null : label
}

/** Same, for when the base host has to be guessed from the shape of the name. */
function labelUnderAssumedBase(hostname: string): string | null {
  const labels = hostname.split('.')
  const baseLength = labels.at(-1) === 'localhost' ? 1 : 2

  return labels.length === baseLength + 1 ? (labels[0] ?? null) : null
}
